"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const footerLinks = [
  { label: 'Shop All', href: '/products' },
  { label: 'Skincare', href: '/products?category=skincare' },
  { label: 'Makeup', href: '/products?category=makeup' },
  { label: 'Fragrance', href: '/products?category=fragrance' },
  { label: 'Account', href: '/account' },
  { label: 'Privacy', href: '#' },
  { label: 'Terms', href: '#' },
];

const socials = [
  { name: 'Instagram', icon: 'CameraIcon', href: '#' },
  { name: 'Pinterest', icon: 'HeartIcon', href: '#' },
  { name: 'TikTok', icon: 'MusicalNoteIcon', href: '#' },
];

export default function Footer() {
  const [showPB, setShowPB] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined" || typeof fetch === "undefined") return;
    fetch("/site-config.json?_=" + Date.now(), { cache: "no-store" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && typeof d.showPoweredBy === "boolean") setShowPB(d.showPoweredBy); })
      .catch(() => {});
  }, []);

  return (
    <>
    {/* Bounded footer height — mobile stacks vertically so needs more vertical
        room; desktop lays out in a row and can stay tight. Min/max keeps the
        footer a predictable size regardless of content-above emptiness. */}
    <footer className="border-t border-border bg-background min-h-[340px] max-h-[420px] md:min-h-[180px] md:max-h-[240px] flex items-center">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-6">
        {/* Pattern 7: Arc Browser Split */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
          {/* Left: Logo + tagline */}
          <div className="flex flex-col gap-3">
            <Link href="/" className="flex items-center gap-2.5">
              <AppLogo size={32} />
              <span data-ocwp-field="brandName" className="font-display text-lg font-semibold tracking-tight text-foreground">
                SweetSkyn by Kym
              </span>
            </Link>
            <p className="text-sm text-muted-foreground font-light max-w-[200px] leading-relaxed">
              Luxury beauty, delivered.
            </p>
          </div>

          {/* Center: Links */}
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {footerLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 focus:outline-none focus:text-foreground"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right: Socials */}
          <div className="flex items-center gap-4">
            {socials.map((s) => (
              <a
                key={s.name}
                href={s.href}
                aria-label={s.name}
                className="p-2 text-muted-foreground hover:text-accent transition-colors duration-200 focus:outline-none focus:text-accent"
                style={{ minHeight: '44px', minWidth: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Icon name={s.icon as 'CameraIcon'} size={18} />
              </a>
            ))}
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-12 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[13px] font-medium text-muted-foreground">
            © 2026 <span data-ocwp-field="brandName">SweetSkyn by Kym</span>. All rights reserved.
          </p>
          <p className="text-[13px] text-muted-foreground">
            Free shipping on orders over $75 USD / <span className="font-sans">J$</span>12,000
          </p>
        </div>
      </div>
    </footer>
    {/* Powered-by strip — sibling of <footer> so the parent's flex layout
        doesn't push it horizontally. Hide = remove this one element cleanly. */}
    {showPB && (
      <div className="bg-background py-4 text-center">
        <p className="text-[11px] text-muted-foreground italic font-light">
          Powered by{" "}
          <a href="https://offloadhost.com/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors underline decoration-dotted underline-offset-2">
            OffloadHost
          </a>{" "}&times;{" "}
          <a href="https://onclickwp.com/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors underline decoration-dotted underline-offset-2">
            OnClickWP
          </a>
        </p>
      </div>
    )}
    </>
  );
}